import React, { useEffect, useState } from "react";
import { sunboundPayProxy } from '@proxies';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSpinnerContext, useSessionContext } from '@contexts';
import { Constants, helpers, Enums } from "@shared";
import { BeginEnrollament } from ".";
import { Bill, NoBill } from "@components";
import { DateTime } from "luxon";
import currency from "currency.js";
import { useAllowedPaymentTypes } from "../hooks/useAllowedPaymentTypes/useAllowedPaymentTypes";

const Guarantor = () => {

    const { setSpinnerShow } = useSpinnerContext();
    const [tasks, setTasks] = useState(null);
    const navigate = useNavigate(), location = useLocation();
    const { session, setSession, setShowAccountSelector } = useSessionContext();

    const {
        currentBill = null,
        actionableInvoice = null
    } = tasks || {};
    const { idAccount, orgDays } = session;

    const {
      allowedPaymentTypes,
      isLoading: allowedPaymentTypesIsLoading,
      isError: allowedPaymentTypesIsError
    } = useAllowedPaymentTypes();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        (async () => {

            setSpinnerShow(true);
            try {
                const response = await sunboundPayProxy.refreshSession();
                setSession(response);
                setShowAccountSelector(true);
            } catch (error) {
                console.error(error);
            }
            setSpinnerShow(false);
        })();
    }, []);

    const {
        paymentPreferences: { hasDefaultPaymentMethod, defaultPaymentMethod },
        accountPreferences: { isAutoPay },
        creditChecked,
        idPayor,
        safeToday
    } = session;

    useEffect(() => {
        (async () => {

            setSpinnerShow(true);
            try {
                const responseTasks = await sunboundPayProxy.getGuarantorTasks(idAccount);
                setTasks(responseTasks);
            } catch (error) {
                console.error(error);
            }
            setSpinnerShow(false);
        })();
    }, [idAccount]);


    const { hasDefaultPaymentMethodDisabled } = session.paymentPreferences;
    const defaultPaymentMethodInvalid = hasDefaultPaymentMethod && !allowedPaymentTypes.includes(defaultPaymentMethod?.idPaymentType);

    const enroll = () => {
        helpers.navigateStart(location, navigate, Constants.Flows.selection.id, {
            hasDefaultPaymentMethod: hasDefaultPaymentMethod && !hasDefaultPaymentMethodDisabled && !defaultPaymentMethodInvalid,
            isAutoPay,
            creditChecked
        }, {
            account: { ...session },
            actionableInvoice: { ...actionableInvoice }
        });
    };

    const alerts = [];

    if (actionableInvoice) {
        const { isLate, isDue } = actionableInvoice;
        if (isLate)
            alerts.push({
                title: `Payment is Late`,
                content: `Click "Get Started" below to set up your plan and pay.  Because you are past the deadline, you will not be able to use Pay Over Time and must pay immediately.`
            });
        else if (isDue)
            alerts.push({
                title: `Payment Past Due`,
                content: `Click "Get Started" below to set up your plan and pay.  Your payment is past due. Make a payment to avoid late fees or safe money using the Pay Over Time product.`
            });
    } else if (currentBill) {
        const {
            idProductType,
            invitations
        } = currentBill;

        const invitation = invitations.find(i => i.idPayor === idPayor);
        const failedTransaction = invitation.payments.find(p => p.idTransactionStatus === Enums.TransactionStatus.declined);
        const { balance, autodrawDate } = invitation;
        const todayDay = DateTime.fromISO(safeToday);

        if(failedTransaction) {
            const transactionAction = Constants.FailedTransactionsActions.find((action) => action.statusCodes.includes(failedTransaction.transactionStatusCode));
            alerts.push({
                title: `Payment Failed: ${failedTransaction.transactionStatusReason}`,
                content: transactionAction?.action || ``
            });
        }

        const isAutopayLate = todayDay.diff(DateTime.fromISO(autodrawDate), `days`).days >= 0;
        if (currency(balance).intValue !== 0 && isAutopayLate && isAutoPay) {
            alerts.push({
                title: `Make a one time payment`,
                content: `Auto-pay is set for a date that has past. Make a one time payment for this month’s balance.`
            });
        }

        if (!creditChecked && idProductType === Enums.ProductType.SunboundPay)
            alerts.push({
                title: `Credit Check Pending`,
                content: `We are still confirming your eligibility for Pay Over Time. We'll be in touch within 24 hours with approval.`
            });
    }

    if (hasDefaultPaymentMethodDisabled || (!allowedPaymentTypesIsLoading && !allowedPaymentTypesIsError && defaultPaymentMethodInvalid)) {
        alerts.push({
            title: `Payment Method Disabled`,
            content: <>Please <a className="underline" href="/payment-method-settings">Go to Payment Settings</a> and update your payment method in order to continue making payments.</>
        });
    }

    const beginEnrollament = () => {

        const { dueDay } = orgDays;
        const { dueDate: invoiceMonth } = actionableInvoice;
        const dueDate = helpers.compoundDay(invoiceMonth, dueDay);

        return (
            <BeginEnrollament invoice={actionableInvoice} next={enroll} alerts={alerts} dueDate={dueDate} />
        );
    };

    return (
        <>
            {
                currentBill != null && <Bill currentBill={currentBill} alerts={alerts} />
            }
            {
                actionableInvoice && beginEnrollament()
            }
            {
                currentBill === null && actionableInvoice === null && <NoBill alerts={alerts} />
            }
        </>
    );
};

export default Guarantor;
